import React from 'react';
import * as THREE from 'three';
import { Text, Billboard } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { UnitSystem } from 'providers/api';
import { distanceMetricToImperial, pressureMetricToImperial } from 'utils/unitConversions';
import PCNode from '../Nodes/PCNode';
import { isBoosterNode, isChokeNode } from '../Nodes';
import PCSpool from '../Spools/PCSpool';
import { isSpoolVisible } from '../levelAreaFilter';
import { Model } from '../../hooks/useModelState';
import { cadToThreeJsPositionWithOffset } from '../../Scenes/scene-utils';
import pressureImage from '../../pressure.png';
import loopImage from '../../loop.png';
import boosterPumpImage from '../../boosterPump.png';
import ruptureDiskImage from '../../ruptureDisk.svg';
import chokeImage from '../../choke.svg';

const textureLoader = new THREE.TextureLoader();
const pressureTexture = textureLoader.load(pressureImage);
const loopTexture = textureLoader.load(loopImage);
const boosterPumpTexture = textureLoader.load(boosterPumpImage);
const ruptureDiskTexture = textureLoader.load(ruptureDiskImage);
const chokeTexture = textureLoader.load(chokeImage);

const PressureMesh = ({ normalizedPosition, scale }: { normalizedPosition: [number, number, number]; scale: number }) => (
  <group position={[normalizedPosition[0], normalizedPosition[1] + 5 * scale, normalizedPosition[2]]}>
    <sprite scale={[10 * scale, 10 * scale, 1]}>
      <spriteMaterial map={pressureTexture} transparent depthWrite={false} />
    </sprite>
  </group>
);

// The icon is a click target in its own right, not just decoration. A flow loop can sit on a spool with
// no usable length to pick - two nodes imported at the same coordinates make a sub-pixel edge, and a spool
// running collinear with its neighbours is buried under them - so the line is not always reachable by
// raycast. The marker always is, and it is the only handle on those spools in the 3D view.
const LoopMesh = ({ normalizedPosition, scale, onClick }: {
  normalizedPosition: [number, number, number];
  scale: number;
  onClick?: (e: ThreeEvent<MouseEvent>) => void;
}) => (
  <group
    position={[normalizedPosition[0], normalizedPosition[1] + 5 * scale, normalizedPosition[2]]}
    onClick={onClick}
  >
    <sprite scale={[10 * scale, 10 * scale, 1]}>
      <spriteMaterial map={loopTexture} transparent depthWrite={false} />
    </sprite>
  </group>
);

const BoosterPumpMesh = ({ normalizedPosition, scale }: { normalizedPosition: [number, number, number]; scale: number }) => (
  <group position={[normalizedPosition[0], normalizedPosition[1] + 8 * scale, normalizedPosition[2]]}>
    <sprite scale={[6 * scale, 12 * scale, 1]}>
      <spriteMaterial map={boosterPumpTexture} transparent depthWrite={false} />
    </sprite>
  </group>
);

const RuptureDiskMesh = ({ normalizedPosition, scale }: { normalizedPosition: [number, number, number]; scale: number }) => (
  <group position={[normalizedPosition[0], normalizedPosition[1] + 5 * scale, normalizedPosition[2]]}>
    <sprite scale={[10 * scale, 10 * scale, 1]}>
      <spriteMaterial map={ruptureDiskTexture} transparent depthWrite={false} />
    </sprite>
  </group>
);

// choke.svg is 1268x771, unlike the square overlay icons, so the sprite has to keep its aspect ratio
// or the symbol renders squashed.
const CHOKE_ICON_ASPECT = 771 / 1268;

const ChokeStationMesh = ({ normalizedPosition, scale }: { normalizedPosition: [number, number, number]; scale: number }) => (
  <group position={[normalizedPosition[0], normalizedPosition[1] + 5 * scale, normalizedPosition[2]]}>
    <sprite scale={[12 * scale, 12 * scale * CHOKE_ICON_ASPECT, 1]}>
      <spriteMaterial map={chokeTexture} transparent depthWrite={false} />
    </sprite>
  </group>
);

const BackfillPlantOverlay = ({
  node: plantNode,
  onSelect,
  model,
  normalizedPosition,
  scale: normalizedScale,
}: {
  node: PCNode;
  onSelect: (node: PCNode) => void;
  model: Model;
  normalizedPosition: [number, number, number];
  scale: number;
}) => {
  const isActive = model.activeBackfillPlant?.id === plantNode.id;
  const opacity = isActive ? 1.0 : 0.55;
  const overlayMaterialProps = {
    transparent: true,
    opacity,
    depthTest: false,
    depthWrite: false,
  };

  const spriteScale = React.useMemo(() => {
    const nodeValues = Object.values(model.nodes || {});
    if (nodeValues.length === 0) return 20 * normalizedScale;

    const points = nodeValues.map((n) => new THREE.Vector3(-n.coords.x, n.coords.z, n.coords.y));
    const box = new THREE.Box3().setFromPoints(points);
    const size = box.getSize(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y, size.z);
    const baseScale = 1.5;
    const scaleFactor = Math.max(1, maxDimension / 100);
    return baseScale * scaleFactor * normalizedScale;
  }, [model.nodes, normalizedScale]);

  const zOffset = React.useMemo(() => {
    const baseOffset = 11;
    const baseScaleVal = 20;
    return (spriteScale / baseScaleVal) * baseOffset;
  }, [spriteScale]);

  // Procedural paste plant: an elevated rectangular process building beside a silo.
  const s = spriteScale;
  const baseY = -s * 0.5;

  const claddingColor = '#d8dde0';
  const siloColor = '#eef1f2';
  const steelColor = '#737b80';
  const roofColor = '#ffffff';

  // Left: elevated rectangular processing building.
  const buildingX = -s * 0.27;
  const buildingWidth = s * 0.5;
  const buildingDepth = s * 0.34;
  const buildingFrameHeight = s * 0.24;
  const buildingHeight = s * 0.42;
  const buildingBottom = baseY + buildingFrameHeight;
  const buildingLegXOffset = buildingWidth * 0.42;
  const buildingLegZOffset = buildingDepth * 0.42;

  // Right: smooth silo on a conical hopper and steel legs.
  const siloX = s * 0.32;
  const siloRadius = s * 0.17;
  const siloLegHeight = s * 0.22;
  const siloHopperHeight = s * 0.2;
  const siloBodyHeight = s * 0.52;
  const siloHopperBottom = baseY + siloLegHeight;
  const siloBodyBottom = siloHopperBottom + siloHopperHeight;
  const siloLegOffset = siloRadius * 0.58;

  const bridgeStartX = buildingX + buildingWidth / 2;
  const bridgeEndX = siloX - siloRadius;
  const bridgeWidth = bridgeEndX - bridgeStartX;

  return (
    <Billboard
      position={[normalizedPosition[0], normalizedPosition[1] + zOffset, normalizedPosition[2]]}
      follow
      lockX={false}
      lockY={false}
      lockZ={false}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(plantNode);
      }}
    >
      {/* Ground slab */}
      <mesh position={[0, baseY - s * 0.03, 0]}>
        <boxGeometry args={[s * 1.15, s * 0.06, s * 0.55]} />
        <meshStandardMaterial color={steelColor} roughness={0.8} {...overlayMaterialProps} />
      </mesh>

      {/* Elevated rectangular process building */}
      {[
        [-buildingLegXOffset, -buildingLegZOffset],
        [buildingLegXOffset, -buildingLegZOffset],
        [-buildingLegXOffset, buildingLegZOffset],
        [buildingLegXOffset, buildingLegZOffset],
      ].map(([dx, dz]) => (
        <mesh key={`building-leg-${dx}-${dz}`} position={[buildingX + dx, baseY + buildingFrameHeight / 2, dz]}>
          <boxGeometry args={[s * 0.025, buildingFrameHeight, s * 0.025]} />
          <meshStandardMaterial color={steelColor} metalness={0.25} roughness={0.55} {...overlayMaterialProps} />
        </mesh>
      ))}
      <mesh position={[buildingX, buildingBottom, 0]}>
        <boxGeometry args={[buildingWidth * 1.08, s * 0.035, buildingDepth * 1.08]} />
        <meshStandardMaterial color={steelColor} metalness={0.25} roughness={0.55} {...overlayMaterialProps} />
      </mesh>
      <mesh position={[buildingX, buildingBottom + buildingHeight / 2, 0]}>
        <boxGeometry args={[buildingWidth, buildingHeight, buildingDepth]} />
        <meshStandardMaterial color={claddingColor} metalness={0.08} roughness={0.72} {...overlayMaterialProps} />
      </mesh>
      <mesh position={[buildingX, buildingBottom + buildingHeight + s * 0.025, 0]}>
        <boxGeometry args={[buildingWidth * 1.05, s * 0.05, buildingDepth * 1.05]} />
        <meshBasicMaterial color={roofColor} {...overlayMaterialProps} />
      </mesh>
      {/* Cladding bands make the rectangular volume readable at a distance. */}
      {[-0.12, 0, 0.12].map((offset) => (
        <mesh key={`cladding-band-${offset}`} position={[buildingX, buildingBottom + buildingHeight / 2 + s * offset, -buildingDepth / 2 - s * 0.004]}>
          <boxGeometry args={[buildingWidth * 0.94, s * 0.018, s * 0.012]} />
          <meshStandardMaterial color={steelColor} metalness={0.2} roughness={0.55} {...overlayMaterialProps} />
        </mesh>
      ))}

      {/* Connecting service bridge */}
      <mesh position={[bridgeStartX + bridgeWidth / 2, buildingBottom + buildingHeight * 0.42, 0]}>
        <boxGeometry args={[bridgeWidth, s * 0.055, s * 0.1]} />
        <meshStandardMaterial color={steelColor} metalness={0.25} roughness={0.55} {...overlayMaterialProps} />
      </mesh>

      {/* Smooth silo and hopper */}
      {[
        [-siloLegOffset, -siloLegOffset],
        [siloLegOffset, -siloLegOffset],
        [-siloLegOffset, siloLegOffset],
        [siloLegOffset, siloLegOffset],
      ].map(([dx, dz]) => (
        <mesh key={`silo-leg-${dx}-${dz}`} position={[siloX + dx, baseY + siloLegHeight / 2, dz]}>
          <boxGeometry args={[s * 0.025, siloLegHeight, s * 0.025]} />
          <meshStandardMaterial color={steelColor} metalness={0.25} roughness={0.55} {...overlayMaterialProps} />
        </mesh>
      ))}
      <mesh position={[siloX, siloHopperBottom + siloHopperHeight / 2, 0]} rotation={[Math.PI, 0, 0]}>
        <coneGeometry args={[siloRadius, siloHopperHeight, 64]} />
        <meshStandardMaterial color={siloColor} metalness={0.12} roughness={0.58} {...overlayMaterialProps} />
      </mesh>
      <mesh position={[siloX, siloBodyBottom + siloBodyHeight / 2, 0]}>
        <cylinderGeometry args={[siloRadius, siloRadius, siloBodyHeight, 64]} />
        <meshStandardMaterial color={siloColor} metalness={0.12} roughness={0.58} {...overlayMaterialProps} />
      </mesh>
      <mesh position={[siloX, siloBodyBottom + siloBodyHeight + s * 0.018, 0]}>
        <cylinderGeometry args={[siloRadius * 1.04, siloRadius, s * 0.036, 64]} />
        <meshBasicMaterial color={roofColor} {...overlayMaterialProps} />
      </mesh>
      <mesh position={[siloX, siloBodyBottom + siloBodyHeight * 0.52, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[siloRadius * 1.01, s * 0.012, 12, 64]} />
        <meshStandardMaterial color={steelColor} metalness={0.25} roughness={0.5} {...overlayMaterialProps} />
      </mesh>
    </Billboard>
  );
};

interface NodeOverlaysProps {
  nodes: PCNode[];
  hoveredNodeId: string | null;
  model: Model;
  offset: THREE.Vector3;
  scale: number;
  pitPressures: Record<string, number>;
  onPlantIconSelect?: (node: PCNode) => void;
  onSelect: (node: PCNode) => void;
  onSpoolSelect?: (spool: PCSpool) => void;
}

const NodeOverlays = React.memo(({
  nodes,
  hoveredNodeId,
  model,
  offset,
  scale,
  pitPressures,
  onPlantIconSelect,
  onSelect,
  onSpoolSelect,
}: NodeOverlaysProps) => {
  const isImperial = model.project?.unitSystemPreference === UnitSystem.Imperial;

  const formatChokeLength = (value: number) => {
    const converted = isImperial ? distanceMetricToImperial(value) : value;
    const unit = isImperial ? 'ft' : 'm';
    return `${Math.round(converted * 10) / 10} ${unit}`;
  };

  // Choke length is spool-owned, so a friction loop is drawn on the spool it belongs to, at the edge
  // midpoint, not on its endpoint nodes. useDeleteNodes still guards BOTH endpoints, since deleting
  // either one destroys the loop.
  const chokedSpools = React.useMemo(
    () => Object.values(model.spools ?? {})
      .filter((spool) => spool.chokeLength > 0 && spool.pointA && spool.pointB
        && isSpoolVisible(spool, model))
      .map((spool) => ({
        spool,
        spoolId: spool.spoolId,
        metres: spool.chokeLength,
        pos: cadToThreeJsPositionWithOffset(
          {
            x: (spool.pointA.coords.x + spool.pointB.coords.x) / 2,
            y: (spool.pointA.coords.y + spool.pointB.coords.y) / 2,
            z: (spool.pointA.coords.z + spool.pointB.coords.z) / 2,
          },
          offset,
          scale,
        ),
      })),
    [model.spools, model.showInactiveSpools, model.levelAreaFilter, model.spoolDateFilter, offset, scale],
  );

  const overlayNodes = React.useMemo(() => {
    const result: Array<{
      node: PCNode;
      pos: [number, number, number];
      normalizedSize: number;
    }> = [];

    nodes.forEach((node) => {
      const hasOverlay = node.showConstraintArrow
        || node.isPit
        || (node.boosterPressure > 0 && !isBoosterNode(node.id))
        || (node.chokeHead > 0 && !isChokeNode(node.id))
        || node.ruptureDiskPressure > 0
        || node.isPlantNode
        || node.id === hoveredNodeId;

      if (hasOverlay) {
        const pos = cadToThreeJsPositionWithOffset(node.coords, offset, scale);
        result.push({ node, pos, normalizedSize: node.nodeSize * scale });
      }
    });

    return result;
  }, [nodes, offset, scale, hoveredNodeId]);

  // An inspection point measures a length of pipe, so its marker sits on the spool, at the edge midpoint,
  // exactly as a flow loop does.
  const inspectionSpools = React.useMemo(
    () => Object.values(model.spools ?? {})
      .filter((spool) => spool.isInspectionPoint && spool.pointA && spool.pointB
        && isSpoolVisible(spool, model))
      .map((spool) => ({
        spool,
        label: spool.name || spool.inspectionPointName,
        pos: cadToThreeJsPositionWithOffset(
          {
            x: (spool.pointA.coords.x + spool.pointB.coords.x) / 2,
            y: (spool.pointA.coords.y + spool.pointB.coords.y) / 2,
            z: (spool.pointA.coords.z + spool.pointB.coords.z) / 2,
          },
          offset,
          scale,
        ),
      })),
    [model.spools, model.showInactiveSpools, model.levelAreaFilter, model.spoolDateFilter, offset, scale],
  );

  return (
    <>
      {inspectionSpools.map(({ spool, label, pos }) => (
        <React.Fragment key={`inspection-${spool.spoolId}`}>
          <arrowHelper
            args={[
              new THREE.Vector3(0, -1, 0),
              new THREE.Vector3(pos[0], pos[1] + 19 * scale, pos[2]),
              18 * scale,
              0xffcc00,
              9 * scale,
              9 * scale,
            ]}
          />
          {label && (
            <Billboard
              position={[pos[0], pos[1] + 25 * scale, pos[2]]}
              follow
              lockX={false}
              lockY={false}
              lockZ={false}
            >
              <Text
                fontSize={8 * scale}
                color="#ffcc00"
                anchorX="center"
                anchorY="middle"
              >
                {label}
              </Text>
            </Billboard>
          )}
        </React.Fragment>
      ))}
      {overlayNodes.map(({ node, pos, normalizedSize }) => (
        <React.Fragment key={node.id}>
          {node.showConstraintArrow && (
            <arrowHelper
              args={[
                new THREE.Vector3(0, -1, 0),
                new THREE.Vector3(pos[0], pos[1] + normalizedSize + 19 * scale, pos[2]),
                18 * scale,
                0xffcc00,
                9 * scale,
                9 * scale,
              ]}
            />
          )}
          {node.isPit && <PressureMesh normalizedPosition={pos} scale={scale} />}
          {node.isPit && pitPressures[node.id] != null && (
            <Billboard
              position={[pos[0], pos[1] + 12 * scale, pos[2]]}
              follow
              lockX={false}
              lockY={false}
              lockZ={false}
            >
              <Text
                fontSize={2.5 * scale}
                color="#ffcc00"
                anchorX="center"
                anchorY="middle"
              >
                {`${Math.round(isImperial ? pressureMetricToImperial(pitPressures[node.id]) : pitPressures[node.id])} ${isImperial ? 'psi' : 'kPa'}`}
              </Text>
            </Billboard>
          )}
          {node.boosterPressure > 0 && !isBoosterNode(node.id) && (
            <BoosterPumpMesh normalizedPosition={pos} scale={scale} />
          )}
          {node.chokeHead > 0 && !isChokeNode(node.id) && (
            <>
              <ChokeStationMesh normalizedPosition={pos} scale={scale} />
              {/* The icon is centred at 5*scale and is 12*scale*CHOKE_ICON_ASPECT tall, so its top edge is
                  at ~8.65*scale; this keeps the label just clear of it. */}
              <Billboard
                position={[pos[0], pos[1] + 11 * scale, pos[2]]}
                follow
                lockX={false}
                lockY={false}
                lockZ={false}
              >
                <Text
                  fontSize={2.5 * scale}
                  color="#ff9966"
                  anchorX="center"
                  anchorY="middle"
                >
                  {`-${formatChokeLength(node.chokeHead)}`}
                </Text>
              </Billboard>
            </>
          )}
          {node.ruptureDiskPressure > 0 && (
            <RuptureDiskMesh normalizedPosition={pos} scale={scale} />
          )}
          {node.isPlantNode && (
            <BackfillPlantOverlay
              node={node}
              onSelect={onPlantIconSelect ?? onSelect}
              model={model}
              normalizedPosition={pos}
              scale={scale}
            />
          )}
          {node.id === hoveredNodeId && (
            <Billboard
              position={[pos[0], pos[1] + normalizedSize + 5 * scale, pos[2]]}
              follow
              lockX={false}
              lockY={false}
              lockZ={false}
            >
              <Text
                fontSize={8 * scale}
                color="#ffcc00"
                anchorX="center"
                anchorY="middle"
              >
                {node.isStope && model.stopes[node.id]?.length
                ? model.stopes[node.id].map((s) => s.data.stopeName).join(', ')
                : node.nodeName}
              </Text>
            </Billboard>
          )}
        </React.Fragment>
      ))}
      {chokedSpools.map(({ spool, spoolId, metres, pos }) => (
        <React.Fragment key={spoolId}>
          <LoopMesh
            normalizedPosition={pos}
            scale={scale}
            onClick={onSpoolSelect
              ? (e) => {
                e.stopPropagation();
                onSpoolSelect(spool);
              }
              : undefined}
          />
          <Billboard
            position={[pos[0], pos[1] + 12 * scale, pos[2]]}
            follow
            lockX={false}
            lockY={false}
            lockZ={false}
          >
            <Text
              fontSize={2.5 * scale}
              color="#ffcc00"
              anchorX="center"
              anchorY="middle"
            >
              {formatChokeLength(metres)}
            </Text>
          </Billboard>
        </React.Fragment>
      ))}
    </>
  );
});

export default NodeOverlays;
